import { memo, ReactElement, useCallback, useMemo } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { Color } from 'shared/utils/colors';
import { DocumentationViewItemType } from 'store/documentation/model/types';
import { DocumentationRowIcon } from '../DocumentationIcon';
import { getDirectoryRowId, getDocumentationRowId } from './rowId';
import { mergeSx } from 'merge-sx';
import {
  documentationPrimaryTypographySx,
  directoryButtonBaseSx,
  directoryPrimaryTypographySx,
  getChevronSx,
  getDirectoryButtonDisabledSx,
  getDocumentationButtonSx,
  getListItemBorderSx,
  listItemIconSx,
  listItemSelectedSx,
} from './styles';

interface DirectoryRowProps {
  directory: DirectoryModel;
  // Note: selected prop is accepted for API compatibility but intentionally not used.
  // Folders should not highlight on click to avoid confusion with multi-select behavior.
  // Single click navigates into the folder without visual selection.
  selected: boolean;
  isEmptyDirectory?: boolean;
  onRowClick: (id: string) => void;
  onRowDoubleClick: (id: string) => void;
}

const DIRECTORY_PREFIX = DocumentationViewItemType.DIRECTORY;
const FILE_PREFIX = DocumentationViewItemType.FILE;

export const DirectoryRow = memo(function DirectoryRow({
  directory,
  selected,
  isEmptyDirectory = false,
  onRowClick,
  onRowDoubleClick,
}: DirectoryRowProps): ReactElement {
  const theme = useTheme();
  const id = getDirectoryRowId(directory);
  const canEnter = Boolean(directory._id);

  const handleClick = useCallback(() => {
    onRowClick(id);
  }, [id, onRowClick]);

  const handleDoubleClick = useCallback(() => {
    onRowDoubleClick(id);
  }, [id, onRowDoubleClick]);

  const listItemBorderSx = useMemo(() => getListItemBorderSx(theme), [theme]);
  const directoryButtonDisabledSx = useMemo(
    () => getDirectoryButtonDisabledSx(theme),
    [theme]
  );
  const chevronSx = useMemo(() => getChevronSx(theme), [theme]);
  const primaryTypographySx = useMemo(
    () => directoryPrimaryTypographySx(false),
    []
  );
  const iconColor = useMemo(
    () => theme.palette.text.secondary,
    [theme]
  );

  const buttonSx = useMemo(
    () =>
      canEnter
        ? directoryButtonBaseSx
        : mergeSx(directoryButtonBaseSx, directoryButtonDisabledSx),
    [canEnter, directoryButtonDisabledSx]
  );

  return (
    <ListItem disablePadding sx={listItemBorderSx}>
      <ListItemButton
        disabled={!canEnter}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        sx={buttonSx}
      >
        <ListItemIcon sx={listItemIconSx}>
          <DocumentationRowIcon
            type={DIRECTORY_PREFIX}
            iconColorOverride={iconColor}
            isEmptyDirectory={isEmptyDirectory}
          />
        </ListItemIcon>
        <ListItemText
          primary={directory.name}
          primaryTypographyProps={{
            noWrap: true,
            sx: primaryTypographySx,
          }}
        />
        <ChevronRight sx={chevronSx} />
      </ListItemButton>
    </ListItem>
  );
});

interface DocumentationRowProps {
  documentation: DocumentationModel;
  selected?: boolean;
  onRowClick?: (id: string) => void;
}

export const DocumentationRow = memo(function DocumentationRow({
  documentation,
  selected = false,
  onRowClick,
}: DocumentationRowProps): ReactElement {
  const theme = useTheme();
  const id = getDocumentationRowId(documentation);
  const isSelectable = Boolean(onRowClick);

  const handleClick = useCallback(() => {
    if (!onRowClick) return;
    onRowClick(id);
  }, [id, onRowClick]);

  const documentationButtonSx = useMemo(
    () => getDocumentationButtonSx(),
    []
  );
  const documentationPrimarySx = useMemo(
    () => documentationPrimaryTypographySx(selected),
    [selected]
  );
  const listItemBorderSx = useMemo(() => getListItemBorderSx(theme), [theme]);
  const iconColor = useMemo(
    () => (selected ? Color.black : theme.palette.text.secondary),
    [selected, theme]
  );
  const documentationListItemButtonSx = useMemo(
    () =>
      isSelectable
        ? mergeSx(directoryButtonBaseSx, listItemSelectedSx)
        : documentationButtonSx,
    [isSelectable, documentationButtonSx]
  );

  return (
    <ListItem disablePadding sx={listItemBorderSx}>
      <ListItemButton
        selected={isSelectable && selected}
        onClick={isSelectable ? handleClick : undefined}
        sx={documentationListItemButtonSx}
      >
        <ListItemIcon sx={listItemIconSx}>
          <DocumentationRowIcon
            type={FILE_PREFIX}
            iconColorOverride={iconColor}
          />
        </ListItemIcon>
        <ListItemText
          primary={documentation.name}
          primaryTypographyProps={{
            noWrap: true,
            sx: documentationPrimarySx,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
});
